import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Award, Bell, BookOpen, ClipboardList, User } from "lucide-react";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const STATUS_COLORS = {
  Submitted: "text-sky-400 border-sky-500/40",
  "Under Review": "text-amber-400 border-amber-500/40",
  Counselling: "text-violet-400 border-violet-500/40",
  Approved: "text-emerald-400 border-emerald-500/40",
  Enrolled: "text-emerald-400 border-emerald-500/40",
};

export default function Portal() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/student/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  return (
    <div className="bg-navy-base min-h-screen" data-testid="student-portal">
      <SEO title="Student Portal | XTM TECHNOLOGIES" description="Your XTM FutureSkills Academy student dashboard." />
      <section className="relative hero-glow">
        <div className="absolute inset-0 cyber-grid opacity-30" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-orange-400 mb-2">Student Portal</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-white" data-testid="portal-welcome">
            Hello, {user?.name?.split(" ")[0]}
          </h1>
          <p className="text-sm text-slate-400 mt-2">{user?.email}</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-card border border-white/10 rounded-2xl p-7" data-testid="portal-applications">
            <h2 className="font-display text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-orange-400" /> My Applications
            </h2>
            {data?.applications?.length ? (
              <ul className="space-y-3">
                {data.applications.map((a) => (
                  <li key={a.application_id} className="border border-white/10 rounded-xl p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`portal-app-${a.application_id}`}>
                    <div>
                      <div className="font-mono text-sm text-orange-400">{a.application_id}</div>
                      <div className="text-sm text-white font-medium">{a.course_title}</div>
                      <div className="text-xs text-slate-500">Applied {new Date(a.created_at).toLocaleDateString("en-IN")}</div>
                    </div>
                    <span className={`text-xs font-semibold border rounded-full px-3 py-1 ${STATUS_COLORS[a.status] || "text-slate-300 border-white/20"}`} data-testid={`portal-app-status-${a.application_id}`}>
                      {a.status}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-sm text-slate-400">
                No applications yet.{" "}
                <Link to="/admissions" className="text-orange-400 hover:text-orange-300 font-medium" data-testid="portal-apply-link">Apply for a course →</Link>
              </div>
            )}
          </div>

          <div className="bg-card border border-white/10 rounded-2xl p-7" data-testid="portal-certificates">
            <h2 className="font-display text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Award className="w-5 h-5 text-orange-400" /> My Certificates
            </h2>
            {data?.certificates?.length ? (
              <ul className="space-y-3">
                {data.certificates.map((c) => (
                  <li key={c.certificate_id} className="border border-white/10 rounded-xl p-4 text-sm text-slate-300">
                    <span className="font-mono text-orange-400">{c.certificate_id}</span> — {c.course} ({c.completion_date})
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-slate-400">Certificates appear here after course completion. Each carries a QR-verifiable ID.</p>
            )}
          </div>

          <div className="bg-card border border-white/10 rounded-2xl p-7" data-testid="portal-courses">
            <h2 className="font-display text-lg font-semibold text-white mb-3 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-orange-400" /> My Courses & Learning
            </h2>
            <p className="text-sm text-slate-400 leading-relaxed">
              Attendance, timetable, learning materials, assignments, tests and results are activated here once your enrollment is confirmed by the academy.
            </p>
          </div>
        </div>

        <aside className="space-y-6">
          <div className="bg-card border border-white/10 rounded-2xl p-6" data-testid="portal-notifications">
            <h3 className="font-display font-semibold text-white mb-4 flex items-center gap-2"><Bell className="w-4 h-4 text-orange-400" /> Notifications</h3>
            <ul className="space-y-3">
              {(data?.notifications || []).map((n) => (
                <li key={n.id} className="text-xs text-slate-400 border-l-2 border-orange-500/50 pl-3">{n.text}</li>
              ))}
            </ul>
          </div>
          <div className="bg-card border border-white/10 rounded-2xl p-6" data-testid="portal-profile">
            <h3 className="font-display font-semibold text-white mb-3 flex items-center gap-2"><User className="w-4 h-4 text-orange-400" /> Profile</h3>
            <p className="text-sm text-slate-300">{user?.name}</p>
            <p className="text-xs text-slate-500 mb-4">{user?.email} • Role: {user?.role}</p>
            <Button asChild variant="outline" className="w-full border-white/20 text-white hover:bg-white/5 rounded-xl transition-colors" data-testid="portal-counsellor-btn">
              <Link to="/contact">Contact the Academy</Link>
            </Button>
          </div>
        </aside>
      </section>
    </div>
  );
}
